import { Grid, Typography } from '@mui/material';
import { t } from 'i18next';
import React, { useCallback, useEffect, useState } from 'react';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import LoadingOverlay from 'src/shared/components/organisms/loadingOverlay';
import { getAutojobById } from 'src/shared/services/autojobService';
import { GenericDialog } from 'src/shared/components/organisms/genericDialog';
import GridWrapper from 'src/shared/components/organisms/gridWrapper';
import { formatDateAndTime } from 'src/shared/functions/formatDate';
import { AutojobResponse } from '../../models/autojobResponse';

type UpcomingAutojobModalProps = {
  modalOpen: boolean;
  taskId: number;
  onClose: () => void;
};

export const UpcomingAutojobModal = (props: UpcomingAutojobModalProps) => {
  const { modalOpen, taskId, onClose } = props;
  const isMounted = useIsMounted();

  const [loading, setLoading] = useState<boolean>(false);
  const [autojob, setAutojob] = useState<AutojobResponse>();

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getAutojobById(taskId);
      if (isMounted()) {
        setAutojob(res.data);
      }
    } catch (e) {
      ConsoleHelper.log(e);
    } finally {
      if (isMounted()) {
        setLoading(false);
      }
    }
  }, [taskId, isMounted]);

  useEffect(() => {
    if (isMounted()) {
      fetchData();
    }
  }, [fetchData, isMounted]);

  const onHandleClose = React.useCallback(() => {
    onClose();
  }, [onClose]);

  const getScheduleLabel = (increaseNum: number | null | undefined, scheduleType: number | null | undefined): string => {
    if (!increaseNum || !scheduleType) return '—';

    const count = parseInt(String(increaseNum), 10);
    if (Number.isNaN(count) || count === 0) return '—';

    const unitKey = {
      3: 'days',
      4: 'weeks',
      5: 'months',
      6: 'years',
    }[scheduleType];

    if (!unitKey) return '—';

    return t(`dashboard:autojobs.intervalUnits.${unitKey}`, { count });
  };

  const getTriggerLabel = (triggerRaw: string | null | undefined): string => {
    if (!triggerRaw) return '—';

    const [amountStr, unitKey, sourceKey] = triggerRaw.split('|');
    const amount = parseInt(amountStr || '0', 10);

    if (Number.isNaN(amount) || !unitKey || !sourceKey) return '—';

    // Map enhet
    const unitTranslationKey = {
      d: 'days',
      m: 'months',
      y: 'years',
    }[unitKey];

    // Map källa
    const sourceTranslationKey = {
      memberExpireDate: 'memberExpireDate',
      invoiceExpireDate: 'invoiceExpireDate',
      createDate: 'createDate',
      resDate: 'resignationDate',
    }[sourceKey];

    if (!unitTranslationKey || !sourceTranslationKey) return '—';

    return t('dashboard:autojobs.triggerLabel', {
      count: amount,
      unit: t(`dashboard:autojobs.units.${unitTranslationKey}`, { count: amount }),
      source: t(`dashboard:autojobs.sources.${sourceTranslationKey}`),
    });
  };

  const GridItem = ({ label, value }) => {
    return (
      <Grid item xs={12}>
        <Typography variant='caption' sx={{ lineHeight: 1, color: 'text.disabled' }}>
          {label}:
        </Typography>
        <Typography variant='body1' sx={{ fontWeight: 500, lineHeight: 1 }}>
          {value || '—'}
        </Typography>
      </Grid>
    );
  };

  return (
    <GenericDialog
      open={modalOpen}
      dialogTitle={t('dashboard:autojobs.chartHeader')}
      dialogContent={
        <GridWrapper narrow>
          <LoadingOverlay loading={loading} />
          <Grid item xs={12} sm={8}>
            <GridItem label={t('dashboard:autojobs.job')} value={autojob?.taskName} />
          </Grid>

          <Grid item xs={12} sm={4}>
            <GridItem label={t('dashboard:autojobs.nextExecution')} value={formatDateAndTime(autojob?.startTimeStamp)} />
          </Grid>

          <Grid item mb={1} xs={12} sm={12}>
            <GridItem label={t('dashboard:autojobs.interval')} value={getScheduleLabel(autojob?.increaseNum, autojob?.scheduleType)} />
            <GridItem label={t('dashboard:autojobs.trigger')} value={autojob?.custom2 ? getTriggerLabel(autojob?.custom2) : '-'} />
            <GridItem label={t('dashboard:autojobs.latestExecution')} value={formatDateAndTime(autojob?.lastExecuted)} />
          </Grid>
        </GridWrapper>
      }
      onClose={onHandleClose}
      onSave={onHandleClose}
      maxWidth='md'
      showDialogActions={false}
    />
  );
};
